import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Container, Section } from "@/components/ui/section";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

const pricingTiers = [
  {
    name: "Free",
    price: "$0",
    period: "/month",
    description: "Perfect for getting started",
    interviews: "Up to 10 customer interviews",
    features: [
      "Collateral hosting",
      "Basic templates",
      "Email collection",
      "Casey branding included"
    ],
    cta: "Get Started Free",
    popular: false
  },
  {
    name: "Professional",
    price: "$249",
    period: "/month",
    description: "For growing businesses",
    interviews: "Up to 50 customer interviews",
    features: [
      "Branded collateral hosting",
      "All premium templates",
      "Remove Casey branding",
      "Multi-channel collection",
      "Basic analytics & reporting",
      "CSV export"
    ],
    cta: "Start Professional",
    popular: true
  },
  {
    name: "Business",
    price: "$449",
    period: "/month",
    description: "For scaling organizations",
    interviews: "Up to 100 customer interviews",
    features: [
      "Branded collateral hosting",
      "Custom templates & branding",
      "Advanced analytics & insights",
      "Team collaboration (5 seats)",
      "API access & webhooks",
      "Priority support",
      "CRM integrations"
    ],
    cta: "Start Business",
    popular: false
  }
];

export const Pricing = () => {
  return (
    <Section id="pricing" className="bg-muted/30">
      <Container>
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the plan that fits your customer interview needs
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {pricingTiers.map((tier, index) => (
            <Card 
              key={index} 
              className={`relative flex flex-col h-full ${tier.popular ? 'border-primary shadow-lg scale-105' : ''}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl font-bold">{tier.price}</span>
                  <span className="text-muted-foreground">{tier.period}</span>
                </div>
                <p className="text-sm text-muted-foreground">{tier.description}</p>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="font-medium text-primary">{tier.interviews}</p>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6 flex-1 flex flex-col">
                <ul className="space-y-3 flex-1">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full ${tier.popular ? 'bg-primary hover:bg-primary/90' : 'variant-outline'}`}
                  asChild
                >
                  <Link to="/early-access">
                    {tier.cta}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Need more than 100 customer interviews per month?
          </p>
          <Button variant="outline" asChild>
            <Link to="/early-access">Contact Sales for Enterprise</Link>
          </Button>
        </div>
      </Container>
    </Section>
  );
};